import { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getStoreContext, getStoreNamespacedKey } from '../utils/storeUtils';

const CartContext = createContext();

export const useCart = () => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
};

export const CartProvider = ({ children }) => {
    const location = useLocation();
    const currentContext = getStoreContext(location.pathname);

    // Load cart synchronously on mount to avoid race condition with save effect
    const [cart, setCart] = useState(() => {
        const storageKey = getStoreNamespacedKey('edisplay_cart', currentContext);
        const savedCart = localStorage.getItem(storageKey);
        if (savedCart) {
            try {
                return JSON.parse(savedCart);
            } catch (e) {
                console.error('Failed to parse cart from localStorage on mount', e);
                return [];
            }
        }
        return [];
    });
    const [cartOpen, setCartOpen] = useState(false);
    const [activeContext, setActiveContext] = useState(currentContext);

    // Load cart from localStorage when context changes (or on mount)
    useEffect(() => {
        const storageKey = getStoreNamespacedKey('edisplay_cart', currentContext);
        const savedCart = localStorage.getItem(storageKey);
        if (savedCart) {
            try {
                setCart(JSON.parse(savedCart));
            } catch (e) {
                console.error('Failed to parse cart from localStorage', e);
                setCart([]);
            }
        } else {
            setCart([]);
        }
        setActiveContext(currentContext);
    }, [currentContext]);

    // Save cart to localStorage on change
    useEffect(() => {
        if (activeContext === currentContext) {
            const storageKey = getStoreNamespacedKey('edisplay_cart', currentContext);
            localStorage.setItem(storageKey, JSON.stringify(cart));
        }
    }, [cart, activeContext, currentContext]);

    const addToCart = (product) => {
        setCart(prev => {
            // Check if item already exists with SAME options (variants, addons, customizations)
            // For now, let's just use product_id for simplicity, but in a real app 
            // we'd need a stable hash of the configuration.
            const existing = prev.find(item =>
                item.product_id === product.product_id &&
                JSON.stringify(item.customizations) === JSON.stringify(product.customizations) &&
                JSON.stringify(item.selectedAddons) === JSON.stringify(product.selectedAddons)
            );

            if (existing) {
                return prev.map(item =>
                    item === existing
                        ? { ...item, quantity: item.quantity + (product.quantity || 1) }
                        : item
                );
            }
            return [...prev, { ...product, quantity: product.quantity || 1 }];
        });
        setCartOpen(true);
    };

    const removeFromCart = (productId, uniqueKey = null) => {
        setCart(prev => {
            // 1. Identify the item(s) being removed to get their uniqueKeys
            const itemsToRemove = prev.filter(item => {
                if (uniqueKey) return item.uniqueKey === uniqueKey;
                return item.product_id === productId;
            });

            // 2. Get keys of removed items
            const removedKeys = itemsToRemove.map(item => item.uniqueKey);

            // 3. Filter out:
            //    a) The item itself
            //    b) Any item that lists a removed key as its parent
            return prev.filter(item => {
                const isTarget = uniqueKey ? item.uniqueKey === uniqueKey : item.product_id === productId;
                const isOrphaned = item.linked_parent_key && removedKeys.includes(item.linked_parent_key);
                return !isTarget && !isOrphaned;
            });
        });
    };

    const updateQuantity = (productId, delta, uniqueKey = null) => {
        setCart(prev => prev.map(item => {
            const matches = uniqueKey ? item.uniqueKey === uniqueKey : item.product_id === productId;
            if (matches) {
                const newQty = item.quantity + delta;
                return newQty > 0 ? { ...item, quantity: newQty } : null;
            }
            return item;
        }).filter(Boolean));
    };

    const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);
    const cartSubtotal = cart.reduce((sum, item) => sum + (item.total_price || item.base_price || 0) * item.quantity, 0);

    const clearCart = () => {
        setCart([]);
    };

    return (
        <CartContext.Provider value={{
            cart,
            cartOpen,
            setCartOpen,
            addToCart,
            removeFromCart,
            updateQuantity,
            clearCart,
            cartCount,
            cartSubtotal
        }}>
            {children}
        </CartContext.Provider>
    );
};
